import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../api';
import toast from 'react-hot-toast';

export interface FrictionMetrics {
  step_identifier: string;
  step_name: string;
  friction_score: number | null;
  friction_level: string | null;
  friction_color: string | null;
  error_rate?: number | null;
  manual_hours_weekly?: number | null;
  cycle_time_hours?: number | null;
  handoff_count?: number | null;
  exception_rate?: number | null;
  normalized_scores?: {
    error: number;
    effort: number;
    time: number;
    handoff: number;
    exception: number;
  } | null;
  data_source?: string;
  confidence_score?: number | null;
  has_data: boolean;
}

export interface FrictionSummary {
  total_steps: number;
  steps_with_data: number;
  average_friction: number;
  critical_count: number;
  high_count: number;
  moderate_count: number;
  low_count: number;
}

export interface FrictionData {
  steps: FrictionMetrics[];
  summary: FrictionSummary;
  config: {
    weights: {
      error: number;
      effort: number;
      time: number;
      handoff: number;
      exception: number;
    };
    maxValues: {
      error_rate: number;
      manual_hours: number;
      cycle_time: number;
      handoffs: number;
      exception_rate: number;
    };
  };
  thresholds: {
    low: { min: number; max: number };
    moderate: { min: number; max: number };
    high: { min: number; max: number };
    critical: { min: number; max: number };
  };
  colors: {
    low: string;
    moderate: string;
    high: string;
    critical: string;
  };
}

interface UseFrictionMetricsResult {
  frictionData: FrictionData | null;
  frictionMap: Map<string, FrictionMetrics>;
  isLoading: boolean;
  isEstimating: boolean;
  error: string | null;
  fetchFrictionData: (processMapId: number) => Promise<number>;
  estimateFriction: (processMapId: number) => Promise<void>;
  updateStepFriction: (processMapId: number, stepIdentifier: string, metrics: Partial<FrictionMetrics>) => Promise<void>;
  clearFrictionData: () => void;
}

export function useFrictionMetrics(): UseFrictionMetricsResult {
  const [frictionData, setFrictionData] = useState<FrictionData | null>(null);
  const [frictionMap, setFrictionMap] = useState<Map<string, FrictionMetrics>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildFrictionMap = useCallback((steps: FrictionMetrics[]) => {
    const map = new Map<string, FrictionMetrics>();
    steps.forEach(step => {
      map.set(step.step_identifier, step);
      if (step.step_name) {
        map.set(step.step_name, step);
        const numMatch = step.step_name.match(/^Step\s+(\S+)/i);
        if (numMatch) map.set(numMatch[1], step);
      }
    });
    setFrictionMap(map);
  }, []);

  const fetchFrictionData = useCallback(async (processMapId: number): Promise<number> => {
    if (!processMapId) return 0;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/friction-metrics/process-map/${processMapId}`);
      const data = response.data as FrictionData;
      setFrictionData(data);
      buildFrictionMap(data.steps);
      return data.steps?.length ?? 0;
    } catch (err: any) {
      console.error('Error fetching friction data:', err);
      setError(err.response?.data?.error || 'Failed to fetch friction data');
      setFrictionData(null);
      setFrictionMap(new Map());
      return 0;
    } finally {
      setIsLoading(false);
    }
  }, [buildFrictionMap]);

  const estimatingRef = useRef(false);

  const estimateFriction = useCallback(async (processMapId: number) => {
    if (!processMapId || estimatingRef.current) {
      // If already estimating, just wait and fetch when done
      if (estimatingRef.current) {
        toast('Estimation already in progress, please wait...', { icon: '⏳' });
      }
      return;
    }

    estimatingRef.current = true;
    setIsEstimating(true);
    setError(null);
    const toastId = toast.loading('Estimating friction metrics... This may take a few minutes.');

    try {
      const response = await api.post(`/api/friction-metrics/estimate/${processMapId}`, {}, {
        timeout: 600000
      });
      const result = response.data;

      toast.dismiss(toastId);
      toast.success(`Estimated friction for ${result.estimated_count || 0} steps`);

      // Always fetch fresh data after estimation
      await fetchFrictionData(processMapId);
    } catch (err: any) {
      toast.dismiss(toastId);
      console.error('Error estimating friction:', err);

      if (err.code === 'ECONNABORTED') {
        toast.error('Estimation timed out. Please try again.');
      } else {
        const errorMsg = err.response?.data?.error || 'Failed to estimate friction';
        toast.error(errorMsg);
        setError(errorMsg);
      }

      // Even on error, try to fetch any partial data that may exist
      try { await fetchFrictionData(processMapId); } catch { /* ignore */ }
    } finally {
      estimatingRef.current = false;
      setIsEstimating(false);
    }
  }, [fetchFrictionData]);

  const updateStepFriction = useCallback(async (
    processMapId: number,
    stepIdentifier: string,
    metrics: Partial<FrictionMetrics>
  ) => {
    try {
      await api.post(
        `/api/friction-metrics/step/${processMapId}/${encodeURIComponent(stepIdentifier)}`,
        metrics
      );
      toast.success('Friction metrics updated');
      await fetchFrictionData(processMapId);
    } catch (err: any) {
      console.error('Error updating step friction:', err);
      toast.error(err.response?.data?.error || 'Failed to update friction metrics');
    }
  }, [fetchFrictionData]);

  const clearFrictionData = useCallback(() => {
    setFrictionData(null);
    setFrictionMap(new Map());
    setError(null);
  }, []);

  return {
    frictionData,
    frictionMap,
    isLoading,
    isEstimating,
    error,
    fetchFrictionData,
    estimateFriction,
    updateStepFriction,
    clearFrictionData
  };
}

export default useFrictionMetrics;
